/**
 * Simple store to cache filter option metadata for dynamic filters.
 * This allows chips (and their hover tooltip) to display proper labels — plus a
 * color dot / avatar when available — instead of raw IDs.
 * Cached to localStorage to survive page refreshes.
 */

const STORAGE_KEY = 'filter-labels-cache';

/** Cached metadata for one filter option value. */
export interface CachedFilterOption {
  label: string;
  color?: string;
  avatar?: string | null;
}

type FilterLabelsCache = Map<string, Map<string, CachedFilterOption>>; // filterName -> (value -> option)

// Initialize cache from localStorage. Supports both the legacy shape
// (value -> string) and the current shape (value -> CachedFilterOption).
function loadFromStorage(): FilterLabelsCache {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const cache: FilterLabelsCache = new Map();
      for (const [filterName, options] of Object.entries(parsed)) {
        const optionMap = new Map<string, CachedFilterOption>();
        for (const [value, entry] of Object.entries(options as Record<string, unknown>)) {
          // Migrate legacy string entries to the richer record shape.
          optionMap.set(
            value,
            typeof entry === 'string' ? { label: entry } : (entry as CachedFilterOption)
          );
        }
        cache.set(filterName, optionMap);
      }
      return cache;
    }
  } catch {
    // Ignore storage errors
  }
  return new Map();
}

// Save cache to localStorage
function saveToStorage(cache: FilterLabelsCache): void {
  try {
    const obj: Record<string, Record<string, CachedFilterOption>> = {};
    for (const [filterName, options] of cache.entries()) {
      obj[filterName] = Object.fromEntries(options.entries());
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // Ignore storage errors
  }
}

const cache: FilterLabelsCache = loadFromStorage();

/**
 * Set label for a filter option
 */
export function setFilterLabel(filterName: string, value: string, label: string): void {
  if (!cache.has(filterName)) {
    cache.set(filterName, new Map());
  }
  cache.get(filterName)!.set(value, { label });
  saveToStorage(cache);
}

/**
 * Set multiple options for a filter. Accepts color/avatar so chips can render a
 * color dot or avatar alongside the label in the hover tooltip.
 */
export function setFilterLabels(
  filterName: string,
  options: Array<{ value: string; label: string; color?: string; avatar?: string | null }>
): void {
  if (!cache.has(filterName)) {
    cache.set(filterName, new Map());
  }
  const filterCache = cache.get(filterName)!;
  options.forEach(opt =>
    filterCache.set(opt.value, { label: opt.label, color: opt.color, avatar: opt.avatar })
  );
  saveToStorage(cache);
}

/**
 * Get label for a filter option
 */
export function getFilterLabel(filterName: string, value: string): string | undefined {
  return cache.get(filterName)?.get(value)?.label;
}

/**
 * Get the full cached option (label + color/avatar) for a filter value.
 */
export function getFilterOption(filterName: string, value: string): CachedFilterOption | undefined {
  return cache.get(filterName)?.get(value);
}

/**
 * Get all cached labels for a filter (value -> label).
 */
export function getFilterLabelsMap(filterName: string): Map<string, string> {
  const optionMap = cache.get(filterName);
  if (!optionMap) return new Map();
  return new Map(Array.from(optionMap.entries(), ([value, opt]) => [value, opt.label]));
}

/**
 * Clear cache for a specific filter
 */
export function clearFilterLabels(filterName: string): void {
  cache.delete(filterName);
  saveToStorage(cache);
}

/**
 * Clear all cached labels
 */
export function clearAllFilterLabels(): void {
  cache.clear();
  localStorage.removeItem(STORAGE_KEY);
}
